// controllers/usePortofolioController.js
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useSession } from '../context/SessionContext';

export default function usePortofolioController() {
  const { session } = useSession();
  const [portofolios, setPortofolios] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch portofolios with images
  const fetchPortofolios = async () => {
    try {
      setLoading(true);
      const { data: portofolios, error: portofoliosError } = await supabase
        .from('portofolio')
        .select('*')
        .eq('user_id', session.user.id);

      if (portofoliosError) throw portofoliosError;

      // Fetch images for each portofolio
      const portofoliosWithImages = await Promise.all(
        portofolios.map(async (porto) => {
          const { data: images, error: imagesError } = await supabase
            .from('image')
            .select('path')
            .eq('category_id', porto.id)
            .eq('category', 'portofolio') // Filter by category

          if (imagesError) throw imagesError;

          // Generate public URL for each image
          const imagesWithPublicUrl = images.map((image) => ({
            ...image,
            publicUrl: supabase.storage.from('document').getPublicUrl(image.path).data.publicUrl,
          }));

          return { ...porto, images: imagesWithPublicUrl };
        })
      );

      setPortofolios(portofoliosWithImages);
    } catch (error) {
      console.error('Error fetching portofolios:', error);
      setError('Failed to fetch portofolios');
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data: categories, error: categoriesError } = await supabase
        .from('porto_category') // Corrected table name
        .select('*')
        .eq('user_id', session.user.id) // Filter by user_id

      if (categoriesError) throw categoriesError;

      setCategories(categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  // Add a new portofolio with images
  const addPortofolio = async (portofolioData, images) => {
    try {
      setLoading(true);

      // Insert portofolio data
      const { data: portofolio, error: portofolioError } = await supabase
        .from('portofolio')
        .insert([{ ...portofolioData, user_id: session.user.id }])
        .select()
        .single();

      if (portofolioError) throw portofolioError;

      // Upload images and insert into image table
      if (images && images.length > 0) {
        await Promise.all(
          images.map(async (image) => {
            // Pastikan ini adalah File baru (bukan URL string)
            if (!(image instanceof File)) {
              return; // Lewati jika bukan File
            }
      
            const fileExt = image.name.split('.').pop();
            const fileName = `${Math.random()}.${fileExt}`;
            const filePath = `${fileName}`;
      
            // Upload image to storage
            const { error: uploadError } = await supabase.storage
              .from('document')
              .upload(filePath, image);
      
            if (uploadError) throw uploadError;

            // Insert image metadata into image table
            await supabase.from('image').insert({
              user_id: session.user.id,
              category: 'portofolio',
              category_id: portofolio.id,
              path: filePath,
            });
          })
        );
      }

      // Refresh portofolios list
      await fetchPortofolios();
    } catch (error) {
      console.error('Error adding portofolio:', error);
      setError('Failed to add portofolio');
    } finally {
      setLoading(false);
    }
  };

  // Update an existing portofolio with images
  const updatePortofolio = async (id, portofolioData, images) => {
    try {
      setLoading(true);

      // Update portofolio data
      const { error } = await supabase
        .from('portofolio')
        .update({ ...portofolioData })
        .eq('id', id)
        .single();

      if (error) throw error;

      // Upload new images and insert into image table
      if (images && images.length > 0) {
        await Promise.all(
          images.map(async (image) => {
            const fileExt = image.name.split('.').pop();
            const fileName = `${Math.random()}.${fileExt}`;
            const filePath = `${fileName}`;

            // Upload image to storage
            const { error: uploadError } = await supabase.storage
              .from('document')
              .upload(filePath, image);

            if (uploadError) throw uploadError;

            // Insert image metadata into image table
            await supabase.from('image').insert({
              user_id: session.user.id,
              category: 'portofolio',
              category_id: id,
              path: filePath,
            });
          })
        );
      }

      // Refresh portofolios list
      await fetchPortofolios();
    } catch (error) {
      console.error('Error updating portofolio:', error);
      setError('Failed to update portofolio');
    } finally {
      setLoading(false);
    }
  };

  // Delete a portofolio and its associated images
  const deletePortofolio = async (id) => {
    try {
      setLoading(true);

      // Fetch associated images
      const { data: images, error: imagesError } = await supabase
        .from('image')
        .select('path')
        .eq('category_id', id);

      if (imagesError) throw imagesError;

      // Delete images from storage
      if (images && images.length > 0) {
        await Promise.all(
          images.map(async (image) => {
            const { error: deleteError } = await supabase.storage
              .from('document')
              .remove([image.path]);

            if (deleteError) throw deleteError;
          })
        );
      }

      // Delete image metadata from database
      await supabase.from('image').delete().eq('category_id', id);

      // Delete portofolio data
      const { error } = await supabase
        .from('portofolio')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Refresh portofolios list
      await fetchPortofolios();
    } catch (error) {
      console.error('Error deleting portofolio:', error);
      setError('Failed to delete portofolio');
    } finally {
      setLoading(false);
    }
  };

  // Delete an image
  const deleteImage = async (imagePath) => {
    try {
      // Delete image from storage
      const { error: deleteError } = await supabase.storage
        .from('document')
        .remove([imagePath]);

      if (deleteError) throw deleteError;

      // Delete image metadata from database
      await supabase.from('image').delete().eq('path', imagePath);

      // Refresh portofolios list
      await fetchPortofolios();
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  };

  // Tambahkan fungsi ini di dalam usePortofolioController.js
const addCategory = async (categoryData) => {
  try {
    setLoading(true);
    const { data: category, error } = await supabase
      .from('porto_category')
      .insert([{ 
        ...categoryData, 
        user_id: session.user.id 
      }])
      .select()
      .single();

    if (error) throw error;

    // Refresh categories list
    await fetchCategories();
    return category;
  } catch (error) {
    console.error('Error adding category:', error);
    setError('Failed to add category');
    throw error;
  } finally {
    setLoading(false);
  }
};

  // Fetch portofolios and categories on component mount
  useEffect(() => {
    if (session) {
      fetchPortofolios();
      fetchCategories();
    }
  }, [session]);

  return {
    portofolios,
    categories,
    loading,
    error,
    addCategory,
    addPortofolio,
    updatePortofolio,
    deletePortofolio,
    deleteImage,
  };
}